import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ProjectService } from '../../../core/services/project.service';

@Component({
    selector: 'app-projects',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './projects.component.html',
    styleUrl: './projects.component.scss'
})
export class ProjectsComponent implements OnInit {
    private projectService = inject(ProjectService);
    private fb = inject(FormBuilder);
    private cdr = inject(ChangeDetectorRef);

    projects: any[] = [];
    isLoading = true;
    errorMessage = '';

    // Modal state
    showModal = false;
    isEditMode = false;
    editingProjectId: string | null = null;

    projectForm = this.fb.group({
        name: ['', Validators.required],
        description: [''],
        status: [1] // Default to Active
    });

    ngOnInit() {
        this.loadProjects();
    }

    loadProjects() {
        this.isLoading = true;
        console.log('Loading projects from:', '/api/reporting/projects');
        this.projectService.getAll().subscribe({
            next: (data: any) => {
                console.log('Projects API Response:', data);
                console.log('Response type:', typeof data);
                console.log('Is Array:', Array.isArray(data));

                const items = Array.isArray(data) ? data : (data?.data || data?.items || []);
                console.log('Parsed items:', items);
                console.log('Items count:', items.length);

                this.projects = items;
                this.isLoading = false;
                this.cdr.detectChanges();
            },
            error: (err: any) => {
                console.error('Failed to load projects - Full error:', err);
                console.error('Error status:', err.status);
                console.error('Error message:', err.message);
                console.error('Error URL:', err.url);
                this.errorMessage = `Failed to load projects: ${err.status} ${err.statusText}`;
                this.isLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    openCreateModal() {
        this.isEditMode = false;
        this.editingProjectId = null;
        this.projectForm.reset({ status: 1 });
        this.showModal = true;
    }

    openEditModal(project: any) {
        this.isEditMode = true;
        this.editingProjectId = project.id;
        this.projectForm.patchValue({
            name: project.name,
            description: project.description,
            status: project.status
        });
        this.showModal = true;
    }

    closeModal() {
        this.showModal = false;
        this.projectForm.reset();
    }

    onSubmit() {
        if (this.projectForm.valid) {
            this.isLoading = true;
            const projectData = {
                ...this.projectForm.value,
                status: Number(this.projectForm.value.status)
            };

            const request = this.isEditMode
                ? this.projectService.update(this.editingProjectId!, projectData)
                : this.projectService.create(projectData);

            request.subscribe({
                next: () => {
                    this.isLoading = false;
                    this.closeModal();
                    this.loadProjects();
                    console.log(`Project ${this.isEditMode ? 'updated' : 'created'} successfully`);
                },
                error: (err: any) => {
                    console.error('Failed to save project', err);
                    this.errorMessage = 'Failed to save project. Please try again.';
                    this.isLoading = false;
                    this.cdr.detectChanges();
                }
            });
        }
    }

    deleteProject(id: string) {
        if (confirm('Are you sure you want to delete this project?')) {
            this.isLoading = true;
            this.projectService.delete(id).subscribe({
                next: () => {
                    this.loadProjects();
                    console.log('Project deleted successfully');
                },
                error: (err: any) => {
                    console.error('Failed to delete project', err);
                    this.errorMessage = 'Failed to delete project. Please try again.';
                    this.isLoading = false;
                    this.cdr.detectChanges();
                }
            });
        }
    }

    changeStatus(id: string, newStatus: number) {
        this.isLoading = true;
        this.projectService.updateStatus(id, newStatus).subscribe({
            next: () => {
                const project = this.projects.find(p => p.id === id);
                if (project) {
                    project.status = newStatus;
                }
                this.isLoading = false;
                this.cdr.detectChanges();
                console.log('Project status updated');
            },
            error: (err: any) => {
                console.error('Failed to update status', err);
                this.errorMessage = 'Failed to update status. Please try again.';
                this.isLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    getStatusLabel(status: number): string {
        switch (status) {
            case 1: return 'Active';
            case 2: return 'Completed';
            case 3: return 'Archived';
            default: return 'Unknown';
        }
    }
}
